import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion, type TargetAndTransition } from 'framer-motion'
import { CircleCheckBig, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DndContext, DragOverlay, useDraggable, useSensor, useSensors, type DragMoveEvent, type DragStartEvent } from '@dnd-kit/core'
import { getEventCoordinates } from '@dnd-kit/utilities'
import { AreaIcon } from '../../../icons/AreaIcon'
import { AutoHeight } from '../../../ui/AutoHeight'
import { DormantIcon, QuickNotesFilledIcon, RecurringIcon, UpcomingIcon } from '../../../icons'
import { useDataSource } from '../../../../lib/dataSource'
import type { LifeSpace, Task } from '../../../../lib/db/models'
import { favoriteItems, favoriteKey, favoriteTaskPlace, reorderedFavoriteOrder, type FavoriteItem, type FavoriteTaskPlace } from '../../../../lib/favorites'
import { setPendingPageJump, setPendingQuickNotesJump, setPendingTaskJump } from '../../../../lib/taskJump/pendingTaskJump'
import type { Tab, TabIcon } from '../../../../hooks/multiWindow'
import { SidebarSection } from '../SidebarSection'
import { ProjectSidebarItem } from '../ProjectSidebarItem'
import type { ProjectSidebarItemProps } from '../ProjectSidebarItem'
import { ProjectDragOverlay } from '../ProjectDragOverlay'
import { ProjectDropIndicator } from '../ProjectDropIndicator'
import { useSidebarData } from '../SidebarDataContext'
import { ProjectPointerSensor } from '../ProjectsSection/projectPointerSensor'
import { cardBesideCursor } from '../ProjectsSection/cardBesideCursor'
import { FavoriteTaskItem, FavoriteTaskSign, type FavoriteTaskItemProps } from './FavoriteTaskItem'
import './FavoritesSection.scss'

export interface FavoritesSectionProps {
  lifeSpace: LifeSpace
  activeTab?: Tab
  openTab: (tab: Tab) => void
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPaths: Set<string>
  previewPaths: Set<string>
  pinnedPaths: Set<string>
  onOpenTab: (e: React.MouseEvent, id: string, path: string, title: string, icon?: TabIcon) => void
  onDoubleClick: (path: string) => void
  onPromote?: () => void
}

/** Was beim Anfassen feststeht: wer gezogen wird und wo die Zeilen stehen. */
interface DragSession {
  item: FavoriteItem
  rows: { key: string; top: number; bottom: number }[]
  listTop: number
}

type RowElement = (key: string, element: HTMLElement | null) => void

const noop = () => {}

/** Die Seiten, auf denen eine Aufgabe ohne Projekt steht (`favoriteTaskPlace`) — wie ihre Sidebar-Einträge. */
const PAGES: Record<Exclude<FavoriteTaskPlace['kind'], 'project'>, { segment: string; titleKey: string; icon: unknown }> = {
  'quick-notes': { segment: 'quick-notes', titleKey: 'sidebar.quickNotes', icon: QuickNotesFilledIcon },
  upcoming: { segment: 'upcoming', titleKey: 'sidebar.upcoming', icon: UpcomingIcon },
  dormant: { segment: 'dormant', titleKey: 'sidebar.dormant', icon: DormantIcon },
  recurring: { segment: 'recurring', titleKey: 'sidebar.recurring', icon: RecurringIcon },
  done: { segment: 'done', titleKey: 'sidebar.done', icon: CircleCheckBig },
}

/*
  Ein neuer Eintrag kommt wie der Inhalt einer aufgeklappten Gruppe (`sectionSlideDown` in
  SidebarSection): 4px von oben, eingeblendet — mit Verzug, damit erst die Höhe Platz macht
  (die Gruppe misst ihren Inhalt und fährt mit, `SidebarSection`). Beim Entfernen geht erst der Eintrag, dann fällt die Höhe zusammen.
*/
const ROW_MOTION: { initial: TargetAndTransition; animate: TargetAndTransition; exit: TargetAndTransition } = {
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut', delay: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } },
}
const ROW_STILL: typeof ROW_MOTION = {
  initial: { opacity: 1 },
  animate: { opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 0, transition: { duration: 0 } },
}

/** Macht eine Zeile zum Griff für dnd-kit und meldet ihr Element zum Messen. */
function useFavoriteDragHandle(key: string, onRowElement: RowElement) {
  const { setNodeRef, listeners } = useDraggable({ id: key })
  return useMemo(() => ({
    ref: (element: HTMLElement | null) => {
      setNodeRef(element)
      onRowElement(key, element)
    },
    onPointerDown: listeners?.onPointerDown as ((event: React.PointerEvent<HTMLElement>) => void) | undefined,
  }), [setNodeRef, onRowElement, key, listeners])
}

const DraggableFavoriteProject: React.FC<ProjectSidebarItemProps & { rowKey: string; onRowElement: RowElement }> = ({ rowKey, onRowElement, ...props }) => (
  <ProjectSidebarItem {...props} dragHandle={useFavoriteDragHandle(rowKey, onRowElement)} />
)

const DraggableFavoriteTask: React.FC<FavoriteTaskItemProps & { rowKey: string; onRowElement: RowElement }> = ({ rowKey, onRowElement, ...props }) => (
  <FavoriteTaskItem {...props} dragHandle={useFavoriteDragHandle(rowKey, onRowElement)} />
)

/**
 * **Die Gruppe „Favoriten“** der Sidebar (#210, CONTEXT.md → Favorit): Projekte und Aufgaben des
 * Lebensbereichs in EINER Liste, in der Reihenfolge des Favorisierens, per Ziehen frei umzusortieren.
 * Fehlt, solange der Bereich keinen Favoriten hat. Ein Projekt öffnet sich wie im Baum; eine Aufgabe
 * springt an ihren Platz (`favoriteTaskPlace`: Projekt, Kurz notiert, Demnächst, Ruhend, Wiederkehrend
 * oder Geschafft), ausgewählt, mit offenen Aufgaben-Details, wo die Seite welche hat.
 *
 * Die Gruppe fährt auf und zu, ein neuer Eintrag blendet ein — nicht beim ersten Laden.
 *
 * @example
 * <FavoritesSection lifeSpace="home" activeTab={activeTab} openTab={openTab} openPaths={openPaths}
 *   previewPaths={previewPaths} pinnedPaths={pinnedPaths} onOpenTab={handleOpenTab}
 *   onDoubleClick={handleDoubleClick} />
 */
export const FavoritesSection: React.FC<FavoritesSectionProps> = ({
  lifeSpace,
  activeTab,
  openTab,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openPaths,
  previewPaths,
  pinnedPaths,
  onOpenTab,
  onDoubleClick,
  onPromote,
}) => {
  const { t } = useTranslation()
  const source = useDataSource()
  // Aus dem Halter oberhalb des Wechsels (`SidebarDataProvider`): Die Sidebar steht immer da, die
  // Abfragen pausieren nie, und ein Neu-Einhängen startet nicht bei `undefined`.
  const { projects, spaceProjects, favoriteTasks: tasks } = useSidebarData(lifeSpace)
  const projectsById = useMemo(() => new Map((spaceProjects ?? []).map(p => [p.id, p])), [spaceProjects])
  const items = useMemo(() => favoriteItems({
    projects: projects ?? [],
    tasks: tasks ?? [],
    lifeSpace,
    lifeSpaceProjectIds: new Set(projectsById.keys()),
  }), [projects, tasks, lifeSpace, projectsById])

  /* ── Sprung an den Platz der Aufgabe ── */

  const placeName = useCallback((place: FavoriteTaskPlace) => (place.kind === 'project'
    ? (projectsById.get(place.projectId)?.name || t('project.defaultName'))
    : t(PAGES[place.kind].titleKey)), [projectsById, t])

  const openTaskInPlace = useCallback((task: Task, e: React.MouseEvent | null) => {
    const place = favoriteTaskPlace(task)
    const title = placeName(place)
    let path: string
    let icon: LucideIcon
    let baseId: string
    // Erst merken, dann öffnen: Die Seite holt den Sprung ab, sobald sie steht (oder schon stand).
    if (place.kind === 'project') {
      path = `/home/${lifeSpace}/project/${place.projectId}`
      icon = AreaIcon as unknown as LucideIcon
      baseId = `${lifeSpace}-project-${place.projectId}`
      setPendingTaskJump(place.projectId, task.id, { openDetails: true })
    } else {
      const page = PAGES[place.kind]
      path = `/home/${lifeSpace}/${page.segment}`
      icon = page.icon as LucideIcon
      baseId = `${lifeSpace}-${page.segment}`
      if (place.kind === 'quick-notes') setPendingQuickNotesJump(lifeSpace, task.id, { openDetails: true })
      else setPendingPageJump(place.kind, lifeSpace, task.id, { openDetails: true })
    }
    if (e) onOpenTab(e, baseId, path, title, icon)
    else openTab({ id: `${baseId}-${Date.now()}`, path, title, icon, lifeSpace, isDirty: false, isPinned: false })
  }, [placeName, lifeSpace, onOpenTab, openTab])

  const unstarProject = useCallback((id: string) => { void source.updateProject(id, { favoriteOrder: null }) }, [source])
  const unstarTask = useCallback((id: string) => { void source.updateTask(id, { favoriteOrder: null }) }, [source])

  /* ── Umsortieren: die Linie folgt dem Zeiger, losgelassen bekommt nur der Gezogene eine neue Stelle ── */

  const sensors = useSensors(useSensor(ProjectPointerSensor, { activationConstraint: { distance: 4 } }))
  const listRef = useRef<HTMLDivElement>(null)
  const rowElements = useRef(new Map<string, HTMLElement>())
  const onRowElement = useCallback<RowElement>((key, element) => {
    if (element) rowElements.current.set(key, element)
    else rowElements.current.delete(key)
  }, [])
  const [session, setSession] = useState<DragSession | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!session) return
    document.body.classList.add('project-tree-dragging')
    return () => document.body.classList.remove('project-tree-dragging')
  }, [session])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const item = items.find(i => favoriteKey(i) === event.active.id)
    const list = listRef.current
    if (!item || !list) return
    const rows = items.flatMap(i => {
      const rect = rowElements.current.get(favoriteKey(i))?.getBoundingClientRect()
      return rect ? [{ key: favoriteKey(i), top: rect.top, bottom: rect.bottom }] : []
    })
    setSession({ item, rows, listTop: list.getBoundingClientRect().top })
    setDropIndex(null)
  }, [items])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const pointer = getEventCoordinates(event.activatorEvent)
    if (!session || !pointer) return
    const y = pointer.y + event.delta.y
    const rest = session.rows.filter(r => r.key !== favoriteKey(session.item))
    setDropIndex(rest.filter(r => (r.top + r.bottom) / 2 < y).length)
  }, [session])

  const endDrag = useCallback(() => {
    setSession(null)
    setDropIndex(null)
  }, [])

  const handleDragEnd = useCallback(() => {
    const drop = session && dropIndex !== null ? { item: session.item, index: dropIndex } : null
    endDrag()
    if (!drop) return
    const key = favoriteKey(drop.item)
    // An der alten Stelle losgelassen: nichts schreiben.
    if (items.findIndex(i => favoriteKey(i) === key) === drop.index) return
    const favoriteOrder = reorderedFavoriteOrder(items, key, drop.index, Date.now())
    // Kein ⌘Z: ADR 0028 nimmt Reihenfolgen aus.
    if (drop.item.kind === 'project') void source.updateProject(drop.item.id, { favoriteOrder })
    else void source.updateTask(drop.item.id, { favoriteOrder })
  }, [session, dropIndex, endDrag, items, source])

  const lineTop = useMemo(() => {
    if (!session || dropIndex === null) return null
    const rest = session.rows.filter(r => r.key !== favoriteKey(session.item))
    if (rest.length === 0) return null
    const y = dropIndex === 0 ? rest[0].top : rest[Math.min(dropIndex, rest.length) - 1].bottom
    return y - session.listTop
  }, [session, dropIndex])

  const reducedMotion = useReducedMotion()
  const rowMotion = reducedMotion ? ROW_STILL : ROW_MOTION

  // Solange die Abfragen noch laden, gibt es nichts, das „erscheinen“ könnte: Was beim ersten
  // Stand schon da ist, steht einfach da (`AnimatePresence initial={false}`).
  if (projects === undefined || spaceProjects === undefined || tasks === undefined) return null

  const renderItem = (item: FavoriteItem) => {
    const key = favoriteKey(item)
    const dragging = session !== null && favoriteKey(session.item) === key
    if (item.kind === 'project') {
      return (
        <DraggableFavoriteProject
          key={key}
          rowKey={key}
          onRowElement={onRowElement}
          project={item.project}
          lifeSpace={lifeSpace}
          activeTab={activeTab}
          inTree={false}
          expanded={false}
          onToggleExpand={noop}
          openPaths={openPaths}
          previewPaths={previewPaths}
          pinnedPaths={pinnedPaths}
          onOpenTab={onOpenTab}
          onDoubleClick={onDoubleClick}
          onPromote={onPromote}
          onCloseTabByPath={onCloseTabByPath}
          onUnpinByPath={onUnpinByPath}
          onPinByPath={onPinByPath}
          openTab={openTab}
          favorite
          onFavoriteChange={favorite => { if (!favorite) unstarProject(item.id) }}
          // Abgeschlossen oder archiviert: bleibt stehen, gedimmt (gemerkt ist gemerkt).
          dimmed={item.project.status !== 'active'}
          dragging={dragging}
        />
      )
    }
    const { task } = item
    return (
      <DraggableFavoriteTask
        key={key}
        rowKey={key}
        onRowElement={onRowElement}
        title={task.title}
        completed={task.status === 'completed'}
        place={placeName(favoriteTaskPlace(task))}
        dragging={dragging}
        onOpen={e => openTaskInPlace(task, e)}
        onOpenInNewTab={() => openTaskInPlace(task, null)}
        onRemove={() => unstarTask(task.id)}
      />
    )
  }

  const dragged = session?.item
  return (
    <AnimatePresence initial={false}>
      {items.length > 0 && (
        // `flow-root`: Der Abstand unter der Gruppe gehört zur gemessenen Höhe, sonst fehlte er im Stand.
        // `instantFollow`: Auf- und Zuklappen fährt die Gruppe selbst (`SidebarSection`); die Hülle
        // fährt nur noch das Erscheinen und Verschwinden der ganzen Gruppe.
        <AutoHeight key="favorites" appear instantFollow bodyClassName="favorites-section__body">
          <SidebarSection id={`${lifeSpace}-favorites`} title={t('sidebar.favorites')} defaultExpanded>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              onDragCancel={endDrag}
            >
              <div ref={listRef} className="favorites-list">
                <AnimatePresence initial={false}>
                  {items.map(item => (
                    <motion.div key={favoriteKey(item)} initial={rowMotion.initial} animate={rowMotion.animate} exit={rowMotion.exit}>
                      {renderItem(item)}
                    </motion.div>
                  ))}
                </AnimatePresence>
                {lineTop !== null && <ProjectDropIndicator top={lineTop} depth={0} />}
              </div>
              {createPortal(
                <DragOverlay dropAnimation={null} modifiers={[cardBesideCursor]}>
                  {dragged && (dragged.kind === 'project'
                    ? <ProjectDragOverlay project={dragged.project} subprojectCount={0} />
                    : (
                      <ProjectDragOverlay
                        project={{ name: dragged.task.title, icon: null, iconColor: null }}
                        subprojectCount={0}
                        leading={<FavoriteTaskSign completed={dragged.task.status === 'completed'} />}
                      />
                    ))}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          </SidebarSection>
        </AutoHeight>
      )}
    </AnimatePresence>
  )
}
